import { QueryConstant, QueryExpr, QueryOperation } from '@apis/Resources';
import styled from '@emotion/styled';
import { Box, Button, Center, Group, Select, Space, Stack, Tabs } from '@mantine/core';
import { Calendar, CalendarProps } from '@mantine/dates';
import { EventEmitter, useEvent } from '@root/Services/EventEmitter';
import { NamedFormats, useFmtSvc } from '@root/Services/FormatService';
import { asExpr, asOperation, exprBuilder, IFluentQueryAdapter } from '@root/Services/QueryExpr';
import { useCallback, useMemo, useRef, useState } from 'react';
import { Calendar as CalendarIcon, Calculator, Check } from 'tabler-icons-react';
import { createAccessor, getInputSpec, InputSpecBuilder } from '../Settings/InputSpec';
import { InputSpecInput, SettingsInput } from '../Settings/SettingsInputs';

interface IBaseDateExprPickerProps<TValueType> {
    value?: TValueType;
    onChange?: (value: TValueType) => void;
    calendarProps?: CalendarProps<false>;
}
interface IPrimitiveDatePicker extends IBaseDateExprPickerProps<null | Date> {
    mode: 'primitive';
}

type ExprDateValidUnits = Parameters<IFluentQueryAdapter<Date>['addDate']>[2];
interface IExprDatePicker extends IBaseDateExprPickerProps<QueryExpr | undefined> {
    mode: 'expr';
    disallowRelative?: boolean;
    units?: ExprDateValidUnits[];
    format?: NamedFormats;
}
export function DateExprPickerContent(props: IExprDatePicker): JSX.Element;
export function DateExprPickerContent(props: IPrimitiveDatePicker): JSX.Element;
export function DateExprPickerContent(props: IExprDatePicker | IPrimitiveDatePicker): JSX.Element {
    const { mode } = props;
    if (mode === 'primitive') {
        return <PrimitiveDatePickerContent {...(props as IPrimitiveDatePicker)} />;
    } else {
        return <ExprDatePickerContent {...(props as IExprDatePicker)} />;
    }
}

function PrimitiveDatePickerContent(props: IPrimitiveDatePicker) {
    const { calendarProps, onChange, value } = props;

    return <Calendar {...calendarProps} value={value} onChange={onChange} />;
}

function ExprDatePickerContent(props: IExprDatePicker) {
    const { calendarProps, onChange, format, value, disallowRelative, units } = props;
    const fmtSvc = useFmtSvc();
    const [initialAbsDate, initialRelDate, initialMode] = useMemo(() => {
        const rawExpr = asExpr(value);
        const rawDateValue = !rawExpr ? value : 'Value' in rawExpr ? rawExpr.Value : null;
        const normDateValue = typeof rawDateValue === 'string' ? fmtSvc.parseDateNoTime(rawDateValue) : rawDateValue;
        const absDateExpr: QueryConstant<Date | null> = { Value: normDateValue };

        const { builder: xb } = exprBuilder<{}>();
        const addDateExpr = asOperation(rawExpr, 'adddate');
        const relDateExpr =
            addDateExpr ?? (xb.resolve(xb.addDate(xb.snapDate(xb.currentDate(), 'day', 'start'), xb.param(0), 'day')) as QueryOperation);

        const mode = addDateExpr ? 'rel' : ('abs' as const);

        return [absDateExpr, relDateExpr, mode] as const;
    }, []);
    const [absDate, setAbsDate] = useState(initialAbsDate);
    const onAbsDateChange = useCallback(
        (dateExpr: QueryConstant<null | Date>) => {
            setAbsDate(dateExpr);
            onChange?.(dateExpr);
        },
        [setAbsDate, onChange]
    );
    const [relDate, setRelDate] = useState(initialRelDate);
    const onRelDateChange = useCallback(
        (value: QueryOperation) => {
            onChange?.(value);
        },
        [relDate]
    );
    const [mode, setMode] = useState<'abs' | 'rel'>(initialMode);

    return (
        <>
            {disallowRelative ? null : <ModePicker mode={mode} onChange={setMode} />}
            <ContentEl>
                {mode === 'abs' || disallowRelative ? (
                    <ExprDatePickerAbsContent calendarProps={calendarProps} value={absDate} onChange={onAbsDateChange} />
                ) : (
                    <ExprDatePickerRelContent {...{ format, units }} onChange={setRelDate} onDone={onRelDateChange} value={relDate} />
                )}
            </ContentEl>
        </>
    );
}

const ContentEl = styled.div`
    height: 325px;
    width: 265px;
`;

interface IExprDatePickerAbsContentProps {
    calendarProps?: CalendarProps;
    value: QueryConstant<Date | null>;
    onChange: (value: QueryConstant<Date | null>) => void;
}
function ExprDatePickerAbsContent(props: IExprDatePickerAbsContentProps) {
    const { calendarProps, onChange: onExprChange, value: valueExpr } = props;

    const value = valueExpr.Value;
    const onChange = useCallback(
        (date: Date | null) => {
            onExprChange({ Value: date });
        },
        [onExprChange]
    );

    return <Calendar firstDayOfWeek="sunday" {...calendarProps} value={value} onChange={onChange} />;
}

interface IExprDatePickerRelContentProps {
    format?: NamedFormats;
    onDone: (value: QueryOperation) => void;
    value: QueryOperation;
    onChange: (value: QueryOperation) => void;
}
function ExprDatePickerRelContent(props: IExprDatePickerRelContentProps) {
    const { onChange: onExprChange, format, onDone, value: valueExpr } = props;

    const signModelRef = useRef<{ value: 'neg' | 'pos' }>({ value: 'pos' });

    const { addDateExpr, amtAcc } = useMemo(() => {
        const { builder: xb } = exprBuilder<{}>();
        const defaultSnap = format === 'short-month' ? 'month' : 'day';
        const addDateExpr = (asOperation(valueExpr, 'adddate') ??
            xb.resolve(xb.addDate(xb.snapDate(xb.currentDate(), defaultSnap, 'start'), xb.param(0), defaultSnap))) as QueryOperation;

        const amtAcc = createAccessor((op: QueryOperation) => op.Operands[1].Value as number);
        signModelRef.current.value = amtAcc.get(addDateExpr) < 0 ? 'neg' : 'pos';

        return { addDateExpr, amtAcc };
    }, [JSON.stringify(valueExpr)]);

    const { addDateUnitOptions, snapUnitOptions, signOptions } = useMemo(() => {
        type UnitOption = { value: 'day' | 'week' | 'month'; label: string };
        const snapUnitOptions: UnitOption[] = [];
        const addDateUnitOptions: UnitOption[] = [];
        const addUnitPlural = Math.abs(amtAcc.get(addDateExpr)) === 1 ? '' : 's';

        if (format !== 'short-month') {
            snapUnitOptions.push({ value: 'day', label: 'Current Date' }, { value: 'week', label: 'Start of Current Week' });
            addDateUnitOptions.push({ value: 'day', label: 'Day' + addUnitPlural }, { value: 'week', label: 'Week' + addUnitPlural });
        }
        snapUnitOptions.push({ value: 'month', label: 'Start of Current Month' });
        addDateUnitOptions.push({ value: 'month', label: 'Month' + addUnitPlural });

        const signOptions = [
            { value: 'pos', label: 'Plus' },
            { value: 'neg', label: 'Minus' },
        ];

        return { addDateUnitOptions, snapUnitOptions, signOptions };
    }, [format, amtAcc.get(addDateExpr)]);

    const [signSpec, amtSpec, addUnitSpec, snapUnitSpec, onChange, apply] = useMemo(() => {
        const sb = InputSpecBuilder.create<QueryOperation>();
        const onChange = EventEmitter.empty();
        const signModel = signModelRef.current;

        const updateExpr = () => {
            onExprChange(addDateExpr);
            onChange.emit();
        };
        const updateSign = (dir: string) => {
            signModel.value = dir as 'neg' | 'pos';
            const sign = dir === 'neg' ? -1 : 1;
            amtAcc.set(addDateExpr, Math.abs(amtAcc.get(addDateExpr)) * sign);
            updateExpr();
        };

        const signSpec = InputSpecBuilder.create<typeof signModel>()
            .type('string')
            .options({ options: signOptions, label: '' })
            .presentation({ align: 'center', width: 75 })
            .map((m) => m.value)
            .bind(signModel, updateSign);

        const addUnitSpec = sb
            .type('string')
            .options({ options: addDateUnitOptions, label: '' })
            .presentation({ align: 'center', width: 85 })
            .map((op) => op.Operands[2].Value as string)
            .bind(addDateExpr, updateExpr);
        const snapUnitSpec = sb
            .type('string')
            .options({ options: snapUnitOptions, label: '' })
            .presentation({ align: 'center', fullWidth: true })
            .map((op) => (op.Operands[0] as QueryOperation).Operands[1].Value as string)
            .bind(addDateExpr, updateExpr);
        const amtSpec = sb
            .type('number')
            .options({ label: '', min: 0, fractions: 0 })
            .presentation({ align: 'center', width: 65 })
            .map((op) => op.Operands[1].Value as number)
            .modelToView((v) => Math.abs(v))
            .viewToModel((v) => v * (signModel.value === 'neg' ? -1 : 1))
            .bind(addDateExpr, updateExpr);

        const apply = () => onDone(addDateExpr);

        return [signSpec, amtSpec, addUnitSpec, snapUnitSpec, onChange, apply] as const;
    }, [addDateExpr, addDateUnitOptions, snapUnitOptions, signOptions]);

    useEvent(onChange);

    return (
        <Stack sx={{ height: '100%' }}>
            <Center sx={{ flex: 1 }}>
                <Box px="md" sx={{ width: '100%' }}>
                    <InputSpecInput spec={snapUnitSpec} />
                    <Space h={4} />
                    <Group noWrap spacing={4}>
                        <InputSpecInput spec={signSpec} />
                        <InputSpecInput spec={amtSpec} />
                        <InputSpecInput spec={addUnitSpec} />
                    </Group>
                </Box>
            </Center>
            <Button variant="white" onClick={apply} leftIcon={<Check size={16} />}>
                Done
            </Button>
        </Stack>
    );
}

function ModePicker(props: { mode: 'abs' | 'rel'; onChange: (mode: 'abs' | 'rel') => void }) {
    const { mode, onChange } = props;
    return (
        <Tabs mb={6} value={mode} onTabChange={onChange}>
            <Tabs.List position="center">
                <Tabs.Tab value="abs" icon={<CalendarIcon size={18} />}>
                    Specific Date
                </Tabs.Tab>
                <Tabs.Tab value="rel" icon={<Calculator size={18} />}>
                    Relative Date
                </Tabs.Tab>
            </Tabs.List>
        </Tabs>
    );
}
